import React, { useEffect, useState, useContext } from "react";
import { Container, Card, Spinner, Button, Badge, Modal, Alert } from "react-bootstrap";
import { ProductContext } from "../context/AppContext";
import Header from "./Header";
import axios from "axios";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { useNetworkStatus } from "../hooks/useNetworkStatus";
import { fetchSchoolTimings, getLateInTime } from "../utils/schoolTime";
import moment from "moment";
import {
  FaCalendarAlt,
  FaBusAlt,
  FaChartPie,
  FaUserCheck,
  FaUserTimes,
  FaWalking,
  FaRegClock,
  FaFlag
} from "react-icons/fa";

// Event attendance dates are date-only values sent as UTC midnight, so the
// date part of the ISO string is the day the school marked.
const toEventDateKey = (value) => {
  if (!value) return null;
  const isoMatch = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch) return isoMatch[1];
  const d = moment(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
};

const extractEventBookings = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.message)) return payload.message;
  if (Array.isArray(payload.data)) return payload.data;
  return [];
};

const Attendance = () => {
  const { GetstudentData, StudentProfileData, Session } = useContext(ProductContext);
  const studentId = GetstudentData?.id;
  const SessionId = GetstudentData?.session;
  const authtoken = localStorage.getItem("token");
  const isTransportOpted = Boolean(StudentProfileData?.is_transport_opted ?? GetstudentData?.is_transport_opted);

  const [activeTab, setActiveTab] = useState('daywise'); // 'daywise' or 'transportwise'
  const [attendanceSummary, setAttendanceSummary] = useState(null);
  const [transportSummary, setTransportSummary] = useState(null);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [transportMonthlyAttendance, setTransportMonthlyAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSlowMsg, message } = useNetworkStatus(loading, 4000);
  const [selectedMonth, setSelectedMonth] = useState(moment());
  const [totalDays, setTotalDays] = useState(0);
  const [presentDays, setPresentDays] = useState(0);
  const [absentDays, setAbsentDays] = useState(0);
  const [leaveDays, setLeaveDays] = useState(0);
  const [notMarkedDays, setNotMarkedDays] = useState(0);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const [transportTotalDays, setTransportTotalDays] = useState(0);
  const [transportPresentDays, setTransportPresentDays] = useState(0);
  const [transportAbsentDays, setTransportAbsentDays] = useState(0);
  const [transportLeaveDays, setTransportLeaveDays] = useState(0);
  const [transportNotMarkedDays, setTransportNotMarkedDays] = useState(0);
  const [transportOverallPercentage, setTransportOverallPercentage] = useState(0);

  const [holidays, setHolidays] = useState([]);
  const classSectionId = GetstudentData?.class_section_id;

  // School timings, used to spot late arrivals (present with in time after school start)
  const [schoolTimings, setSchoolTimings] = useState([]);
  useEffect(() => {
    fetchSchoolTimings(authtoken).then(setSchoolTimings);
  }, []);

  // Event attendance (pickup/dropoff) grouped by YYYY-MM-DD
  const [eventAttendanceByDate, setEventAttendanceByDate] = useState({});

  // Detail modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAttendanceDetail, setSelectedAttendanceDetail] = useState(null);

  useEffect(() => {
    if (!studentId || !SessionId) return;
    if (activeTab === 'daywise') {
      fetchOverallSummary();
      fetchMonthlyAttendance();
    } else if (activeTab === 'transportwise' && isTransportOpted) {
      fetchTransportOverallSummary();
      fetchTransportMonthlyAttendance();
    }
  }, [studentId, SessionId, selectedMonth, activeTab, isTransportOpted]);

  useEffect(() => {
    if (!isTransportOpted && activeTab === 'transportwise') {
      setActiveTab('daywise');
    }
  }, [isTransportOpted, activeTab]);

  useEffect(() => {
    const fetchHolidays = async () => {
      if (!SessionId || !classSectionId) return;
      try {
        const response = await axios.get(
          `https://api.mirai.healthcare/api/masters/holidaycalender?session_id=${SessionId}&class_section_id=${classSectionId}`,
          { headers: { "x-auth": authtoken } }
        );
        if (response.data.success) {
          setHolidays(response.data.data || []);
        }
      } catch (error) {
        console.error("Error fetching holidays:", error);
      }
    };
    fetchHolidays();
  }, [SessionId, classSectionId, authtoken]);

  useEffect(() => {
    const fetchEventAttendance = async () => {
      if (!studentId) return;
      const cacheKey = `attendance_event_bookings_${studentId}`;
      const buildMap = (bookings) => {
        const map = {};
        bookings.forEach((booking) => {
          if (!booking || booking.is_deleted === true) return;
          const eventName = booking?.category?.title?.trim() || 'Event';
          (Array.isArray(booking.eventAttendance) ? booking.eventAttendance : []).forEach((att) => {
            const key = toEventDateKey(att?.date);
            if (!key) return;
            if (!map[key]) map[key] = [];
            map[key].push({ ...att, event_name: att.event_name || eventName });
          });
        });
        return map;
      };

      const cached = getCachedData(cacheKey);
      if (Array.isArray(cached)) setEventAttendanceByDate(buildMap(cached));

      try {
        const res = await axios.get(
          `https://api.mirai.healthcare/api/student-details/event-booking`,
          { params: { student_id: studentId }, headers: { "x-auth": authtoken } }
        );
        const bookings = extractEventBookings(res?.data);
        setEventAttendanceByDate(buildMap(bookings));
        setCachedData(cacheKey, bookings);
      } catch (error) {
        console.error("Error fetching event attendance:", error);
      }
    };
    fetchEventAttendance();
  }, [studentId, authtoken]);

  const fetchOverallSummary = async () => {
    if (!studentId || !SessionId) return;
    const cacheKey = `overall_summary_${studentId}_${SessionId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setTotalDays(cached.totalDays || 0);
      setPresentDays(cached.present || 0);
      setAbsentDays(cached.absent || 0);
      setLeaveDays(cached.leave || 0);
      setNotMarkedDays(cached.notMark || 0);
      setOverallPercentage(cached.totalDays > 0 ? ((cached.present / cached.totalDays) * 100).toFixed(2) : 0);
      setAttendanceSummary(cached);
      setLoading(false);
    }
    setLoading(true);
    try {
      const fromDate = moment().startOf('year').format('YYYY-MM-DD');
      const toDate = moment().endOf('year').format('YYYY-MM-DD');
      const response = await axios.get(
        `https://api.mirai.healthcare/api/attendance/StudentAttendance/filter-data`,
        {
          params: {
            session_id: SessionId,
            student_id: studentId,
            from_date: fromDate,
            to_date: toDate,
          },
          headers: { "x-auth": authtoken },
        }
      );
      if (response.data.success) {
        const data = response.data.data;
        setTotalDays(data.totalDays || 0);
        setPresentDays(data.present || 0);
        setAbsentDays(data.absent || 0);
        setLeaveDays(data.leave || 0);
        setNotMarkedDays(data.notMark || 0);
        setOverallPercentage(data.totalDays > 0 ? ((data.present / data.totalDays) * 100).toFixed(2) : 0);
        setAttendanceSummary(data);
        setCachedData(cacheKey, data);
      }
    } catch (error) {
      console.error("Error fetching overall summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyAttendance = async () => {
    if (!studentId || !SessionId) return;
    const fromDate = selectedMonth.clone().startOf('month').format('YYYY-MM-DD');
    const toDate = selectedMonth.clone().endOf('month').format('YYYY-MM-DD');
    const cacheKey = `monthly_attendance_${studentId}_${SessionId}_${fromDate}_${toDate}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setMonthlyAttendance(cached);
      setLoading(false);
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.mirai.healthcare/api/attendance/StudentAttendance/date-wise?session_id=${SessionId}&student_id=${studentId}&from_date=${fromDate}&to_date=${toDate}`,
        {
          headers: { "x-auth": authtoken },
        }
      );
      if (response.data.success) {
        setMonthlyAttendance(response.data.data || []);
        setCachedData(cacheKey, response.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching monthly attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransportOverallSummary = async () => {
    if (!studentId) return;
    const cacheKey = `transport_overall_${studentId}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setTransportTotalDays(cached.totalDays || 0);
      setTransportPresentDays(cached.present || 0);
      setTransportAbsentDays(cached.absent || 0);
      setTransportLeaveDays(cached.leave || 0);
      setTransportNotMarkedDays(cached.notMark || 0);
      setTransportOverallPercentage(
        cached.totalDays > 0 ? (((cached.present || 0) / cached.totalDays) * 100).toFixed(2) : 0
      );
      setTransportSummary(cached);
      setLoading(false);
    }
    setLoading(true);
    try {
      const matchedSession = Array.isArray(Session?.data)
        ? Session.data.find((s) => s.Is_Current_Session === true || s.is_current === true || s.is_current_session === true) ||
          Session.data.find((s) => Number(s.id) === Number(SessionId))
        : null;

      const rawStartDate =
        matchedSession?.Start_Date ||
        matchedSession?.start_date ||
        matchedSession?.session_start_date ||
        GetstudentData?.session_start_date;

      const getFormattedDate = (dateStr) => {
        if (!dateStr) return null;
        if (typeof dateStr === "string") {
          const trimmed = dateStr.trim();
          const ddmmyyyyMatch = trimmed.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
          if (ddmmyyyyMatch) {
            const [, day, month, year] = ddmmyyyyMatch;
            return `${year}-${month}-${day}`;
          }
          if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return trimmed;
          }
          if (trimmed.includes("T")) {
            return trimmed.split("T")[0];
          }
        }
        const parsed = new Date(dateStr);
        return !isNaN(parsed.getTime()) ? parsed.toISOString().split("T")[0] : null;
      };

      const fromDate = getFormattedDate(rawStartDate) || moment().startOf('year').format('YYYY-MM-DD');
      const toDate = moment().format('YYYY-MM-DD');
      const response = await axios.get(
        `https://api.mirai.healthcare/api/attendance/transportAttendance/transport-attendences-count`,
        {
          params: {
            student_id: studentId,
            from_date: fromDate,
            to_date: toDate,
          },
          headers: { "x-auth": authtoken },
        }
      );
      const data =
        response?.data?.transport_attendance ||
        response?.data?.data ||
        {};

      setTransportTotalDays(data.totalDays || 0);
      setTransportPresentDays(data.present || 0);
      setTransportAbsentDays(data.absent || 0);
      setTransportLeaveDays(data.leave || 0);
      setTransportNotMarkedDays(data.notMark || 0);
      setTransportOverallPercentage(
        data.totalDays > 0 ? (((data.present || 0) / data.totalDays) * 100).toFixed(2) : 0
      );
      setTransportSummary(data);
      setCachedData(cacheKey, data);
    } catch (error) {
      console.error("Error fetching transport overall summary:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransportMonthlyAttendance = async () => {
    if (!studentId) return;
    const fromDate = selectedMonth.clone().startOf('month').format('YYYY-MM-DD');
    const toDate = selectedMonth.clone().endOf('month').format('YYYY-MM-DD');
    const cacheKey = `transport_monthly_${studentId}_${fromDate}_${toDate}`;
    const cached = getCachedData(cacheKey);
    if (cached) {
      setTransportMonthlyAttendance(cached);
      setLoading(false);
    }
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.mirai.healthcare/api/attendance/transportAttendance/login-student`,
        {
          params: {
            student_id: studentId,
            from_date: fromDate,
            to_date: toDate,
          },
          headers: { "x-auth": authtoken },
        }
      );
      const records =
        response?.data?.transport_attendance ||
        response?.data?.data ||
        [];
      const arr = Array.isArray(records) ? records : [];
      setTransportMonthlyAttendance(arr);
      setCachedData(cacheKey, arr);
    } catch (error) {
      console.error("Error fetching transport monthly attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDayClick = (day, record, type, isHoliday, holidayDetails, events = []) => {
    if (!record && !isHoliday && events.length === 0) return;
    const localDate = record?.date ? moment.utc(record.date).local() : selectedMonth.clone().date(day);
    setSelectedAttendanceDetail({
      type: isHoliday && (!record || record.status === 'holiday') ? 'Holiday' : type,
      displayDate: localDate.format('dddd, MMMM D, YYYY'),
      status: isHoliday && (!record || record.status === 'holiday') ? 'Holiday' : (record?.status || record?.attendance_status || 'Not Marked'),
      inTime: record?.in_time || record?.inTime || record?.intime || null,
      outTime: record?.out_time || record?.outTime || record?.outtime || null,
      outStatus: record?.out_status || record?.outStatus || null,
      leaveType: record?.leave_type || record?.leaveType || null,
      lateInTime: type === 'Day Wise' ? getLateInTime(record, schoolTimings, SessionId) : null,
      notes: isHoliday && (!record || record.status === 'holiday') ? holidayDetails?.title : (record?.notes || record?.note || record?.remarks || record?.remark || null),
      description: isHoliday ? holidayDetails?.description : null,
      route: record?.route_name || record?.route || record?.route_id || record?.routeId || null,
      rollNumber: record?.roll_no || record?.roll_number || record?.rollnum || record?.roll_num || null,
      events,
    });
    setShowDetailsModal(true);
  };

  // Events marked on a day: from the booking API, plus any the date-wise
  // attendance API returns on the day's record (deduped by id).
  const getEventsForDay = (day, record) => {
    const key = `${selectedMonth.format('YYYY-MM')}-${String(day).padStart(2, '0')}`;
    const fromBookings = eventAttendanceByDate[key] || [];
    const fromRecord = Array.isArray(record?.event_attendance) ? record.event_attendance : [];
    const seen = new Set();
    return [...fromBookings, ...fromRecord].filter((ev) => {
      const id = ev?.id ?? `${ev?.event_id}-${ev?.attendance_type}-${ev?.trip}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  // Group data by day for calendar (generic function)
  const getCalendarData = (attendanceData, includeEvents = false, markLate = false) => {
  const calendarData = {};
  attendanceData.forEach((item) => {
    // Convert UTC date to local date and get the day
    const localDate = moment.utc(item.date).local();
    const day = localDate.date().toString();
    calendarData[day] = { status: item.status || 'not-marked', record: item };
  });

  // Map holidays
  const holidayMap = {};
  holidays.forEach(holiday => {
    const start = moment(holiday.start_date);
    const end = moment(holiday.end_date);
    if (start.isSameOrBefore(selectedMonth.clone().endOf('month')) &&
        end.isSameOrAfter(selectedMonth.clone().startOf('month'))) {
       let curr = start.clone();
       while (curr.isSameOrBefore(end)) {
         if (curr.month() === selectedMonth.month() && curr.year() === selectedMonth.year()) {
            holidayMap[curr.date().toString()] = holiday;
         }
         curr.add(1, 'day');
       }
    }
  });

  // Generate days for the month
  const daysInMonth = selectedMonth.daysInMonth();
  const startDay = selectedMonth.startOf('month').day(); // 0 = Sunday
  const calendarDays = [];

  // Empty cells for previous month
  for (let i = 0; i < startDay; i++) {
    calendarDays.push({ day: null, status: null, record: null, isHoliday: false, holidayDetails: null });
  }

  // Days of current month
  for (let day = 1; day <= daysInMonth; day++) {
    const dayStr = day.toString();
    const dayData = calendarData[dayStr];
    const holidayData = holidayMap[dayStr];

    let finalStatus = dayData ? dayData.status : 'not-marked';
    let record = dayData ? dayData.record : null;
    let isHoliday = false;
    const events = includeEvents ? getEventsForDay(day, record) : [];

    if (holidayData) {
       if (finalStatus === 'not-marked') {
           finalStatus = 'holiday';
           record = { status: 'holiday', notes: holidayData.title, description: holidayData.description };
       }
       isHoliday = true;
    }

    calendarDays.push({
      day,
      status: finalStatus,
      record: record,
      lateInTime: markLate && !isHoliday ? getLateInTime(record, schoolTimings, SessionId) : null,
      isHoliday,
      holidayDetails: holidayData || null,
      events
    });
  }

  // Fill with next month if needed to make 6 rows (42 cells)
  while (calendarDays.length < 42) {
    calendarDays.push({ day: null, status: null, record: null, isHoliday: false, holidayDetails: null });
  }

  return calendarDays;
};

  const formatStatusLabel = (status) =>
    status ? status.replace(/_/g, ' ').toUpperCase() : 'NOT MARKED';

  const getTransportStatusText = (status, time) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'absent') return 'ABSENT';
    if (statusLower === 'leave') return 'LEAVE';
    if (statusLower === 'holiday') return 'HOLIDAY';
    if (time && time.trim() !== '') return time;
    return formatStatusLabel(status || 'not-marked');
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'present': return 'success';
      case 'absent': return 'danger';
      case 'leave': return 'warning';
      case 'halfday': return 'info';
      case 'holiday': return 'primary';
      case 'not-marked': return 'secondary';
      default: return 'light';
    }
  };

  const getStatusClass = (status) => `bg-${getStatusVariant(status)}`;

  const dayWiseCalendarData = getCalendarData(monthlyAttendance, true, true);
  const transportCalendarData = getCalendarData(transportMonthlyAttendance);
  const isNextDisabled = false;

  const handleMonthChange = (direction) => {
    const targetMonth = selectedMonth.clone().add(direction, 'month');
    setSelectedMonth(targetMonth);
  };

  const handlePrevMonth = () => handleMonthChange(-1);
  const handleNextMonth = () => handleMonthChange(1);

  if (loading) {
    return (
      <div className="page-wrapper">
        <Header />
        <div className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
          <Spinner animation="border" />
        </div>
      </div>
    );
  }

  const renderSummarySection = (isTransport = false) => {
    const total = isTransport ? transportTotalDays : totalDays;
    const present = isTransport ? transportPresentDays : presentDays;
    const absent = isTransport ? transportAbsentDays : absentDays;
    const leave = isTransport ? transportLeaveDays : leaveDays;
    const notMarked = isTransport ? transportNotMarkedDays : notMarkedDays;
    const percentage = isTransport ? transportOverallPercentage : overallPercentage;
    const title = isTransport ? 'Transport Attendance Summary' : 'Attendance Summary';

    const markedDays = present + absent + leave;

const attendancePercentage = markedDays
  ? ((present / markedDays) * 100).toFixed(2)
  : 0;

    const statItems = [
      { label: 'Present', value: present, color: 'success', icon: FaUserCheck },
      { label: 'Absent', value: absent, color: 'danger', icon: FaUserTimes },
      { label: 'Leave', value: leave, color: 'warning', icon: FaWalking },
      { label: 'Not Marked', value: notMarked, color: 'secondary', icon: FaRegClock },
      { label: 'Percentage', value: attendancePercentage, color: 'primary', icon: FaChartPie, isPercentage: true },
    ];
    const SummaryIcon = isTransport ? FaBusAlt : FaCalendarAlt;

    return (
      <Card className="mb-4 shadow-sm border-0 attendance-summary-card">
        <Card.Body className="p-4">
          <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 shadow-sm">

            <div className="icon-box p-2 bg-white rounded d-flex align-items-center justify-content-center shadow-sm">
              <SummaryIcon className="text-primary fs-5" />
            </div>

            <div>
              <h5 className="fw-semibold mb-0">{title}</h5>
            </div>

          </div>



          <div className="summary-scroll mt-4">
            {statItems.map((item) => {
  const Icon = item.icon;

  return (
    <div key={item.label} className="summary-pill flex-shrink-0">
      <div className="d-flex justify-content-between align-items-center mb-2">

        <span className="fw-semibold d-flex align-items-center">
          <Icon className={`me-2 text-${item.color}`} />
          {item.label}
        </span>

        {/* Days OR Percentage */}
        <span className={`badge bg-light text-${item.color}`}>
          {item.isPercentage
            ? `${attendancePercentage} %`
            : `${item.value} Days`}
        </span>

      </div>
    </div>
  );
})}
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderCalendarSection = (calendarData, title, type) => {
    const legendItems = [
      { label: 'Present', color: 'success', icon: FaUserCheck },
      ...(type === 'Day Wise' ? [{ label: 'Late', color: 'late', icon: FaRegClock }] : []),
      { label: 'Absent', color: 'danger', icon: FaUserTimes },
      { label: 'Leave', color: 'warning', icon: FaWalking },
      { label: 'Holiday', color: 'primary', icon: FaCalendarAlt },
      { label: 'Not Marked', color: 'secondary', icon: FaRegClock },
      ...(type === 'Day Wise' ? [{ label: 'Event', color: 'info', icon: FaFlag }] : []),
    ];

    const holidaysInMonth = calendarData.filter(cell => cell.isHoliday).length;

    return (
      <Card className="shadow-sm border-0 attendance-calendar-card">
        <Card.Body className="p-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-3">
            <div>
              <h5 className="mb-1">{title}</h5>
              <small className="text-muted">Tap a day to see complete details</small>
            </div>
            <div className="d-flex align-items-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handlePrevMonth}
                className="me-2"
              >
                &lt; Prev
              </Button>
              <span className="fw-bold mx-2">{selectedMonth.format('MMMM YYYY')}</span>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleNextMonth}
                disabled={isNextDisabled}
              >
                Next &gt;
              </Button>
            </div>
          </div>

          <div className="calendar-legend d-flex flex-wrap gap-2 mb-3">
            {legendItems.map((item) => {
              const Icon = item.icon;
              return (
                <span key={item.label} className={`legend-pill text-${item.color}`}>
                  <Icon className="me-1" />
                  {item.label}
                </span>
              );
            })}
          </div>

          <Alert variant="info" className="py-2 mb-3 d-flex align-items-center">
             <FaCalendarAlt className="me-2" />
             <span><strong>{holidaysInMonth}</strong> {holidaysInMonth === 1 ? 'holiday' : 'holidays'} in {selectedMonth.format('MMMM YYYY')}</span>
          </Alert>

          <div className="calendar-weekdays text-muted fw-semibold mb-2">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <span key={day} className={day === 'Sun' ? 'text-danger' : ''}>{day}</span>
            ))}
          </div>

          <div className="calendar-grid">
            {calendarData.map((cell, index) => (
              <div key={index} className="calendar-cell">
                {cell.day ? (
                  <button
                    type="button"
                    className={`calendar-day ${cell.isHoliday || cell.status === 'holiday' ? 'bg-primary text-white' : type === 'Transport Wise' ? 'transport-day-cell' : cell.lateInTime ? 'bg-late' : getStatusClass(cell.status)} ${(cell.record || cell.isHoliday || cell.events?.length) ? 'clickable' : 'disabled'} ${cell.events?.length ? 'has-event' : ''} ${index % 7 === 0 ? 'is-sunday' : ''}`}
                    onClick={() => handleDayClick(cell.day, cell.record, type, cell.isHoliday, cell.holidayDetails, cell.events || [])}
                    disabled={!cell.record && !cell.isHoliday && !cell.events?.length}
                  >
                    <span className="day-number">{cell.day}</span>
                    {cell.lateInTime && (
                      <span className="late-cell-badge" title={`Late: ${cell.lateInTime}`}>
                        Late
                      </span>
                    )}
                    {cell.events?.length > 0 && (
                      <span
                        className="event-cell-badge"
                        title={cell.events.map((ev) => `${ev.event_name || 'Event'} (${ev.attendance_type || '-'}): ${formatStatusLabel(ev.status)}`).join('\n')}
                      >
                        <FaFlag /> Event
                      </span>
                    )}
                    {cell.isHoliday || cell.status === 'holiday' ? (
                      <div className="holiday-cell-badge" title={`Holiday: ${cell.holidayDetails?.title || cell.record?.notes || 'Holiday'}`}>
                        <span className="holiday-name">{cell.holidayDetails?.title || cell.record?.notes || 'Holiday'}</span>
                      </div>
                    ) : type === 'Transport Wise' && cell.record ? (
                      <div className="transport-cell-details">
                        <div className={`transport-badge-row bg-${getStatusVariant(cell.record.status)}`} title={`Pick (${formatStatusLabel(cell.record.status)}): ${cell.record.in_time || 'N/A'}`}>
                          <span className="badge-type">P:</span>
                          <span className="badge-time">{getTransportStatusText(cell.record.status, cell.record.in_time)}</span>
                        </div>
                        <div className={`transport-badge-row bg-${getStatusVariant(cell.record.out_status || 'not-marked')}`} title={`Drop (${formatStatusLabel(cell.record.out_status || 'not-marked')}): ${cell.record.out_time || 'N/A'}`}>
                          <span className="badge-type">D:</span>
                          <span className="badge-time">{getTransportStatusText(cell.record.out_status, cell.record.out_time)}</span>
                        </div>
                      </div>
                    ) : null}
                  </button>
                ) : (
                  <div className="calendar-day empty" />
                )}
              </div>
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  };

  return (
    <div className="page-wrapper">
      <Header />
      <Container fluid className="mt-4 p-3">
        {/* Tabs - Day Wise / Transport Wise */}
        <div className="attendance-tab-bar d-flex justify-content-center gap-3 mb-4">
          <button
            type="button"
            className={`tab-pill ${activeTab === 'daywise' ? 'active' : ''}`}
            onClick={() => setActiveTab('daywise')}
          >
            <FaChartPie className="me-2" />
            <span>Day Wise</span>

          </button>
          {isTransportOpted && (
            <button
              type="button"
              className={`tab-pill ${activeTab === 'transportwise' ? 'active' : ''}`}
              onClick={() => setActiveTab('transportwise')}
            >
              <FaBusAlt className="me-2" />
              <span>Transport Wise</span>
            </button>
          )}
        </div>

        {activeTab === 'daywise' && (
          <>
            {renderSummarySection(false)}
            {renderCalendarSection(dayWiseCalendarData, 'Monthly Attendance', 'Day Wise')}
          </>
        )}

        {activeTab === 'transportwise' && (
          <>
            {renderSummarySection(true)}
            {renderCalendarSection(transportCalendarData, 'Monthly Transport Attendance', 'Transport Wise')}
          </>
        )}
        <Modal
          show={showDetailsModal}
          onHide={() => setShowDetailsModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title>
              {selectedAttendanceDetail?.type || 'Attendance'} Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedAttendanceDetail ? (
              <div className="attendance-detail-modal">
                {selectedAttendanceDetail.type === 'Transport Wise' ? (
                  <div className="text-center mb-4">
                    <div className="d-flex align-items-center justify-content-center gap-2 mb-2 flex-wrap">
                      <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                        Pick: <Badge bg={getStatusVariant(selectedAttendanceDetail.status)} className="ms-1 text-uppercase">{formatStatusLabel(selectedAttendanceDetail.status)}</Badge>
                      </span>
                      <span className="badge bg-light text-dark border px-3 py-2 rounded-pill">
                        Drop: <Badge bg={getStatusVariant(selectedAttendanceDetail.outStatus)} className="ms-1 text-uppercase">{formatStatusLabel(selectedAttendanceDetail.outStatus || 'not-marked')}</Badge>
                      </span>
                    </div>
                    <div className="fw-semibold mt-2">
                      {selectedAttendanceDetail.displayDate}
                    </div>
                    <small className="text-muted">
                      {selectedAttendanceDetail.type}
                    </small>
                  </div>
                ) : (
                  <div className="text-center mb-4">
                    <Badge bg={getStatusVariant(selectedAttendanceDetail.status)} className="px-3 py-2 rounded-pill text-uppercase">
                      {formatStatusLabel(selectedAttendanceDetail.status)}
                    </Badge>
                    {selectedAttendanceDetail.lateInTime && (
                      <span className="badge bg-late px-3 py-2 rounded-pill text-uppercase ms-2">
                        Late · {selectedAttendanceDetail.lateInTime}
                      </span>
                    )}
                    <div className="fw-semibold mt-2">
                      {selectedAttendanceDetail.displayDate}
                    </div>
                    <small className="text-muted">
                      {selectedAttendanceDetail.type}
                    </small>
                  </div>
                )}
                <div className="attendance-detail-grid">
                  {(() => {
                    if (selectedAttendanceDetail.type === 'Transport Wise') {
                      const pickStatusLower = (selectedAttendanceDetail.status || '').toLowerCase();
                      const dropStatusLower = (selectedAttendanceDetail.outStatus || '').toLowerCase();

                      const isPickAbsent = ['absent', 'leave', 'holiday', 'not-marked', 'not marked'].includes(pickStatusLower);
                      const isDropAbsent = ['absent', 'leave', 'holiday', 'not-marked', 'not marked'].includes(dropStatusLower);

                      return [
                        {
                          label: 'Pick Status',
                          value: (
                            <Badge bg={getStatusVariant(selectedAttendanceDetail.status)} className="px-2 py-1 text-uppercase">
                              {formatStatusLabel(selectedAttendanceDetail.status)}
                            </Badge>
                          ),
                        },
                        {
                          label: 'Pick In Time',
                          value: isPickAbsent ? 'N/A' : (selectedAttendanceDetail.inTime || 'Not Available'),
                        },
                        {
                          label: 'Drop Status',
                          value: (
                            <Badge bg={getStatusVariant(selectedAttendanceDetail.outStatus)} className="px-2 py-1 text-uppercase">
                              {formatStatusLabel(selectedAttendanceDetail.outStatus || 'not-marked')}
                            </Badge>
                          ),
                        },
                        {
                          label: 'Drop Out Time',
                          value: isDropAbsent ? 'N/A' : (selectedAttendanceDetail.outTime || 'Not Available'),
                        },
                        {
                          label: 'Route / Bus',
                          value: selectedAttendanceDetail.route || 'Not Assigned',
                        },
                        {
                          label: 'Roll No.',
                          value: selectedAttendanceDetail.rollNumber || 'Not Available',
                        },
                        {
                          label: 'Notes',
                          value: selectedAttendanceDetail.notes || 'No notes shared',
                          fullWidth: true,
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className={`detail-card ${item.fullWidth ? 'full' : ''}`}
                        >
                          <small className="text-muted text-uppercase">{item.label}</small>
                          <div className="fw-semibold mt-1">{item.value}</div>
                        </div>
                      ));
                    }

                    const statusLower = selectedAttendanceDetail.status?.toLowerCase() || '';
                    const hideTimeAndRoute = ['holiday', 'absent', 'leave', 'not marked', 'not-marked'].includes(statusLower);
                    const hideLeaveType = ['holiday'].includes(statusLower);

                    return [
                      ...(!hideTimeAndRoute ? [
                        { label: 'In Time', value: selectedAttendanceDetail.inTime || 'Not Available' },
                        { label: 'Out Time', value: selectedAttendanceDetail.outTime || 'Not Available' }
                      ] : []),
                      ...(!hideLeaveType ? [
                        { label: 'Leave Type', value: selectedAttendanceDetail.leaveType || 'Not Applied' }
                      ] : []),
                      ...(!hideTimeAndRoute && selectedAttendanceDetail.type !== 'Day Wise' ? [
                        {
                          label: 'Route / Bus',
                          value: selectedAttendanceDetail.route || (selectedAttendanceDetail.type === 'Transport Wise' ? 'Not Assigned' : 'Not Applicable'),
                        },
                        {
                          label: 'Roll No.',
                          value: selectedAttendanceDetail.rollNumber || 'Not Available',
                        }
                      ] : []),
                      {
                        label: 'Notes',
                        value: selectedAttendanceDetail.notes || 'No notes shared',
                        fullWidth: true,
                      },
                      ...(selectedAttendanceDetail.description ? [{
                        label: 'Description',
                        value: selectedAttendanceDetail.description,
                        fullWidth: true,
                      }] : [])
                    ].map((item) => (
                    <div
                      key={item.label}
                      className={`detail-card ${item.fullWidth ? 'full' : ''}`}
                    >
                      <small className="text-muted text-uppercase">{item.label}</small>
                      <div className="fw-semibold mt-1">{item.value}</div>
                    </div>
                  ));
                  })()}
                </div>

                {selectedAttendanceDetail.events?.length > 0 && (
                  <div className="event-attendance-section mt-4">
                    <h6 className="fw-semibold mb-2 d-flex align-items-center gap-2">
                      <FaFlag className="text-info" /> Event Attendance
                    </h6>
                    <div className="d-flex flex-column gap-2">
                      {selectedAttendanceDetail.events.map((ev, idx) => (
                        <div
                          key={ev.id ?? idx}
                          className="detail-card d-flex flex-wrap align-items-center justify-content-between gap-2"
                        >
                          <div>
                            <div className="fw-semibold">{ev.event_name || 'Event'}</div>
                            <small className="text-muted text-capitalize">
                              Type: {ev.attendance_type || 'N/A'}
                              {ev.in_time ? ` · In: ${ev.in_time}` : ''}
                              {ev.out_time ? ` · Out: ${ev.out_time}` : ''}
                            </small>
                          </div>
                          <Badge bg={getStatusVariant(ev.status)} className="px-2 py-1 text-uppercase">
                            {formatStatusLabel(ev.status || 'not-marked')}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="mb-0 text-muted">Select a day to see detailed information.</p>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-0">
            <Button variant="primary" className="ms-auto" onClick={() => setShowDetailsModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      <style jsx>{`
        .attendance-tab-bar {
          flex-wrap: wrap;
        }
        .attendance-detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 1rem;
        }
        .detail-card {
          border: 1px solid #eceff3;
          background: #fdfdfd;
          border-radius: 12px;
          padding: 0.85rem;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
        }
        .detail-card.full {
          grid-column: 1 / -1;
        }
        .attendance-tab-bar .tab-pill {
          border: 1px solid #e5e7eb;
          background: #ffffff;
          padding: 0.75rem 1.25rem;
          border-radius: 999px;
          outline: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: #495057;
          box-shadow: 0 2px 6px rgba(15, 15, 15, 0.08);
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .attendance-tab-bar .tab-pill:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(15, 15, 15, 0.1);
        }

        .attendance-tab-bar .tab-pill.active {
          background: #0d6efd;
          color: #fff;
          border-color: #0d6efd;
          box-shadow: 0 10px 25px rgba(13, 110, 253, 0.25);
        }

        .attendance-tab-bar .tab-pill.active .badge {
          background: rgba(255, 255, 255, 0.22) !important;
          color: #fff !important;
        }

        .attendance-tab-bar .tab-pill:focus-visible {
          outline: 3px solid rgba(13, 110, 253, 0.35);
          outline-offset: 2px;
        }

        .attendance-summary-card {
          border-radius: 16px;
        }

        .summary-top .summary-title h4 {
          font-size: 1.35rem;
        }

        .percentage-ring {
          width: 120px;
          height: 120px;
          border-radius: 50%;
          border: 8px solid rgba(13, 110, 253, 0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0d6efd;
          background: rgba(13, 110, 253, 0.05);
        }

        .percentage-ring small {
          font-size: 0.75rem;
          margin-left: 2px;
        }

        .summary-scroll {
          display: flex;
          gap: 1rem;
          overflow-x: auto;
          padding-bottom: 0.5rem;
          -webkit-overflow-scrolling: touch;
        }

        .summary-scroll::-webkit-scrollbar {
          height: 6px;
        }

        .summary-scroll::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 3px;
        }

        .summary-pill {
          border: 1px solid #f1f1f5;
          border-radius: 14px;
          padding: 1rem;
          min-width: 170px;
          background: #f9fafc;
          box-shadow: 0 4px 14px rgba(15, 15, 15, 0.05);
        }

        .summary-pill .badge {
          padding: 0.35rem 0.65rem;
          font-size: 0.75rem;
          border-radius: 999px;
        }

        .attendance-calendar-card {
          border-radius: 16px;
        }

        .calendar-legend .legend-pill {
          border-radius: 999px;
          background: #f1f3f5;
          padding: 0.35rem 0.85rem;
          font-size: 0.85rem;
          display: inline-flex;
          align-items: center;
        }

        .calendar-weekdays {
          display: grid;
          grid-template-columns: repeat(7, minmax(32px, 1fr));
          text-align: center;
          gap: 0.25rem;
          max-width: 760px;
          margin: 0 auto;
          width: 100%;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, minmax(32px, 1fr));
          gap: 0.35rem;
          max-width: 760px;
          margin: 0 auto;
          width: 100%;
        }

        .calendar-cell {
          position: relative;
        }

        .calendar-day {
          width: 100%;
          aspect-ratio: 1 / 1;
          border: none;
          border-radius: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          padding: 2px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .holiday-cell-badge {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          margin-top: 2px;
          line-height: 1.1;
        }

        .holiday-name {
          font-size: 8px;
          font-weight: 700;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          opacity: 0.95;
          padding: 0 2px;
          text-align: center;
        }

        .calendar-day.transport-day-cell {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #1e293b;
          flex-direction: column;
          justify-content: flex-start;
          align-items: center;
          padding: 3px 2px;
          height: auto;
          min-height: 56px;
          aspect-ratio: auto;
          border-radius: 10px;
        }

        .transport-cell-details {
          display: flex;
          flex-direction: column;
          gap: 2px;
          width: 100%;
          margin-top: 2px;
        }

        .transport-badge-row {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 1px 3px;
          border-radius: 4px;
          font-size: 8px;
          font-weight: 600;
          color: #ffffff;
          line-height: 1.1;
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
        }

        .transport-badge-row.bg-light,
        .transport-badge-row.bg-warning,
        .transport-badge-row.bg-info,
        .transport-badge-row.bg-secondary {
          color: #1e293b;
        }

        .transport-badge-row.bg-success {
          background-color: #16a34a !important;
          color: #ffffff !important;
        }

        .transport-badge-row.bg-danger {
          background-color: #dc2626 !important;
          color: #ffffff !important;
        }

        .transport-badge-row.bg-warning {
          background-color: #d97706 !important;
          color: #ffffff !important;
        }

        .transport-badge-row.bg-secondary {
          background-color: #e2e8f0 !important;
          color: #64748b !important;
        }

        .transport-badge-row .badge-type {
          font-weight: 700;
          font-size: 7.5px;
        }

        .transport-badge-row .badge-time {
          font-size: 7.5px;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .calendar-day.clickable:not(:disabled) {
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(15, 15, 15, 0.1);
        }

        .calendar-day.clickable:not(:disabled):hover {
          transform: translateY(-1px);
        }

        .calendar-day.disabled {
          opacity: 0.55;
          cursor: not-allowed;
          color: #6c757d;
          background: #f1f3f5;
        }

        .calendar-day.empty {
          background: transparent;
        }

        .event-cell-badge {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          margin-top: 2px;
          padding: 1px 4px;
          border-radius: 4px;
          background: #0dcaf0;
          color: #052c65;
          font-size: 8px;
          font-weight: 700;
          line-height: 1.2;
          max-width: 100%;
          overflow: hidden;
          white-space: nowrap;
        }

        .event-cell-badge svg {
          font-size: 7px;
        }

        .calendar-day.has-event {
          outline: 2px solid #0dcaf0;
          outline-offset: -2px;
        }

        .calendar-day .day-number {
          pointer-events: none;
        }

        .calendar-day.bg-success,
        .calendar-day.bg-danger,
        .calendar-day.bg-primary,
        .calendar-day.bg-secondary {
          color: #fff;
        }

        .calendar-day.is-sunday {
          color: #e53e3e;
        }

        /* Late: present with in time after school start */
        .bg-late,
        .calendar-day.bg-late,
        .calendar-day.is-sunday.bg-late {
          background-color: #fd7e14 !important;
          color: #fff;
        }

        .text-late {
          color: #fd7e14 !important;
        }

        .late-cell-badge {
          margin-top: 2px;
          padding: 1px 4px;
          border-radius: 4px;
          background: #fff;
          color: #c2410c;
          font-size: 8px;
          font-weight: 700;
          line-height: 1.2;
        }

        .calendar-day.is-sunday:not(.bg-success):not(.bg-danger):not(.bg-primary):not(.bg-secondary):not(.bg-warning):not(.bg-info) {
          background-color: #fff5f5;
        }

        .calendar-day.is-sunday.bg-success,
        .calendar-day.is-sunday.bg-danger,
        .calendar-day.is-sunday.bg-primary,
        .calendar-day.is-sunday.bg-secondary {
          color: #fff;
        }

        .calendar-day.bg-warning,
        .calendar-day.bg-info,
        .calendar-day.bg-light {
          color: #212529;
        }

        @media (min-width: 992px) {
          .calendar-day {
            max-width: 68px;
            margin: 0 auto;
          }
        }

        @media (max-width: 991px) {
          .summary-top {
            text-align: center;
          }

          .summary-meta {
            text-align: center !important;
          }
        }

        @media (max-width: 576px) {
          .summary-top {
            flex-direction: row !important;
            overflow-x: auto;
            gap: 1rem !important;
            padding-bottom: 0.25rem;
          }

          .summary-top > div {
            flex: 0 0 230px;
          }

          .summary-top::-webkit-scrollbar {
            height: 4px;
          }

          .summary-top::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 999px;
          }

         .attendance-tab-bar {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  width: 100%;
}

          .attendance-tab-bar::-webkit-scrollbar {
            height: 4px;
          }

          .attendance-tab-bar::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 999px;
          }

          .attendance-tab-bar .tab-pill {
            flex: 0 0 auto;
          }

          .summary-pill {
            min-width: 150px;
          }

          .percentage-ring {
            width: 100px;
            height: 100px;
            border-width: 6px;
          }
          .calendar-grid {
            gap: 0.25rem;
            max-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default Attendance;
